<script lang="ts">
	import { onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Button,
		Icon,
		Modal,
		Popover,
		Text,
		toast,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system/index';
	import { mobile } from 'src/services/mobile-rework';
	import ModesStep from './ModesStep.svelte';
	import InstallHeightStep from './InstallHeightStep.svelte';
	import BoundsStep from './BoundsStep.svelte';
	import MeasurementStep from './MeasurementStep.svelte';
	import { devices } from 'src/stores/lists/devices';
import { app, os } from 'src/stores/session/native-bridge';
import { hideBluetoothSettingsToast } from 'src/stores/ui/calibration-modals';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';
	import {
		deviceSlotsParams,
		DISTANCE_ADDITIONAL_PROPS,
		saveDevice,
		updateDeviceDistanceSettings,
	} from 'src/helpers/distance_helper';
	import DistanceRangeStep from './DistanceRangeStep.svelte';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';

	import type { Device } from 'src/types/Device';
	import { drawerState } from 'src/stores/ui/sidepanel-public';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let device;
	export let showModal = false;

	let initialized = false;
	let showExpiredMsg = false;
	let errors: Record<string, string> = {};
	let disabled = false;

	const defaultDeviceData = {
		modeType: device?.modeType || DISTANCE_ADDITIONAL_PROPS.modeType,
		solidsType: device?.solidsType || DISTANCE_ADDITIONAL_PROPS.solidsType,
		garageType: device?.garageType || DISTANCE_ADDITIONAL_PROPS.garageType,
		closedPositionRange:
			device?.closedPositionRange || DISTANCE_ADDITIONAL_PROPS.closedPositionRange,
		closedToleranceRange:
			device?.closedToleranceRange || DISTANCE_ADDITIONAL_PROPS.closedToleranceRange,
		installationHeight: device?.installationHeight || DISTANCE_ADDITIONAL_PROPS.installationHeight,
		reverseMeasurement: device?.reverseMeasurement || DISTANCE_ADDITIONAL_PROPS.reverseMeasurement,
		measureFromBase: device?.measureFromBase || DISTANCE_ADDITIONAL_PROPS.measureFromBase,
		meterRange: device?.triggerDistance || DISTANCE_ADDITIONAL_PROPS.triggerDistance,
		meterRangeStart: device?.meterRangeStart || DISTANCE_ADDITIONAL_PROPS.meterRangeStart,
		meterRangeEnd: device?.meterRangeEnd || DISTANCE_ADDITIONAL_PROPS.meterRangeEnd,
		measureVibration: device?.measureVibration || false,
		distanceRange: `${device.distanceRange}` || '7',
		measureInterval: device?.measureInterval || 30,
		isValid: true,
	};

	let deviceData = { ...defaultDeviceData };

	let baseWizardItems = [
		{
			key: 'modes',
			titleKey: 'distance_wizard_title_modes',
			title: 'Modes',
			subtitleKey: 'distance_wizard_subtitle_modes',
			subtitle: 'Choose Measurement Mode',
			active: true,
			disabled: false,
			showMoreDots: false,
			activeIndex: 0,
		},
		{
			key: 'install_height',
			titleKey: 'distance_wizard_title_install_height',
			title: 'Install Height',
			subtitleKey: 'wizard_sequences_subtitle_install_height',
			subtitle: 'Set Device Installation Height',
			active: false,
			disabled: false,
			showMoreDots: false,
			activeIndex: 1,
		},
		{
			key: 'bounds',
			titleKey: 'distance_wizard_title_bounds',
			title: 'Bounds',
			subtitleKey: 'distance_wizard_subtitle_bounds',
			subtitle: 'Set Meter Empty and Full Points',
			active: false,
			disabled: false,
			showMoreDots: false,
			activeIndex: 2,
		},
		{
			key: 'range',
			titleKey: 'distance_wizard_range',
			title: 'Range',
			subtitleKey: 'distance_wizard_subtitle_distance_range',
			subtitle: 'Set Measurement Range',
			active: false,
			disabled: false,
			showMoreDots: false,
			activeIndex: 3,
		},
		{
			key: 'measurement',
			titleKey: 'option_measurement',
			title: 'Measurement',
			subtitleKey: 'distance_wizard_subtitle_measurement',
			subtitle: 'Set Measurement Interval',
			active: false,
			disabled: false,
			showMoreDots: false,
			activeIndex: 4,
		},
	];

	let wizardItems = [...baseWizardItems];

	const occupancyOverrides = {
		install_height: {
			titleKey: 'distance_wizard_title_baseline',
			title: 'Baseline',
			subtitleKey: 'distance_wizard_subtitle_baseline',
			subtitle: 'Set Empty-Spot Reading',
		},
		bounds: {
			titleKey: 'distance_wizard_title_occupancy',
			title: 'Occupancy',
			subtitleKey: 'distance_wizard_subtitle_occupancy',
			subtitle: 'Choose When the Spot Counts as Occupied',
		},
	};

	const garageOverrides = {
		install_height: {
			titleKey: 'distance_wizard_title_garage_mount',
			title: 'Mount & Aim',
			subtitleKey: 'distance_wizard_subtitle_garage_mount',
			subtitle: 'Follow Mounting Instructions',
		},
		bounds: {
			titleKey: 'sub_settings_title_calibration',
			title: 'Calibration',
			subtitleKey: 'distance_wizard_subtitle_calibration',
			subtitle: 'Set Closed Position Distance',
		},
		measurement: { activeIndex: 3 },
	};

	$: if (deviceData.modeType === 'garage') {
		if (wizardItems.length === 3) wizardItems = [...baseWizardItems];
		wizardItems = wizardItems
			.filter((item) => item.key !== 'range')
			.map((item) => {
				if (garageOverrides[item.key]) return { ...item, ...garageOverrides[item.key] };
				return item;
			});
	} else if (deviceData.modeType === 'occupancy') {
		wizardItems = wizardItems
			.filter((item) => !['range', 'measurement'].includes(item.key))
			.map((item) => {
				if (occupancyOverrides[item.key]) return { ...item, ...occupancyOverrides[item.key] };
				return item;
			});
	} else {
		if ([3, 4].includes(wizardItems.length)) wizardItems = [...baseWizardItems];
	}

	let activeStep = wizardItems[0];

	$: mappedWizardSteps =
		deviceData.modeType === 'garage'
			? {
					0: ModesStep,
					1: InstallHeightStep,
					2: BoundsStep,
					3: MeasurementStep,
					4: DistanceRangeStep,
				}
			: {
					0: ModesStep,
					1: InstallHeightStep,
					2: BoundsStep,
					3: DistanceRangeStep,
					4: MeasurementStep,
				};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', () => onCloseModal());
			return () => mobile.removeEventListener('back_button', () => onCloseModal());
		}
	});

	onDestroy((): void => {
		if (!$app) return;
		// Prevent check if we have been asked to turn on bluetooth
		// but we have declined, we do not send a disconnect command
		if (
			$controlBleDevice.currentStatusIndex === BLU_STATUS.UNHANDLED_STATE &&
			$controlBleDevice.connectionState === BLU_STATUS.BUTTON_PRESS_REQUIRED
		) {
			return;
		}

		if ($controlBleDevice.connectionState === 2) {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'disconnect')
				: control_bluetooth_device_android(device.id, 'disconnect');
			controlBleDevice.reset();
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'abortConnecting')
				: control_bluetooth_device_android(device.id, 'disconnect');
			controlBleDevice.reset();
		}
	});

	const reverbBleProcess = () => {
		if (!$app) return;
		controlBleDevice.updateDeviceId(device.id);
		controlBleDevice.updateProcessRunning(true);
		$os === 'IOS'
			? control_bluetooth_device_ios(device.id, 'connect')
			: control_bluetooth_device_android(device.id, 'connect');
	};

	const onCloseModal = (goTo?: boolean) => {
		if (showModal) {
			showModal = false;
			activeStep = wizardItems[0];
			wizardItems = [...baseWizardItems];
			deviceData = { ...defaultDeviceData };
			if (goTo) {
				drawerState.set({ ...$drawerState, type: 'device', id: device.id });
			}
		}
	};

	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}
			return {
				...item,
				active: false,
			};
		});
	};

	const showSuccessToast = () => {
		toast.open({
			type: 'success',
			title: $format('toast_success_cofigurate_device', {
				default: 'Successful device configuration',
			}),
		});
	};

	const showErrorToast = () => {
		toast.open({
			type: 'danger',
			title: $format('error_global', { default: 'Something went wrong!' }),
		});
	};

	const onCloseWizard = async () => {
		let editedDevice = { ...device };
		editedDevice.onboarded = true;
		editedDevice.modeType = deviceData.modeType;
		editedDevice.solidsType = deviceData.solidsType;
		editedDevice.garageType = deviceData.garageType;
		editedDevice.measureFromBase = deviceData.measureFromBase;
		editedDevice.reverseMeasurement = deviceData.reverseMeasurement;
		editedDevice.installationHeight = +deviceData.installationHeight.toFixed(0);
		if (deviceData.modeType === 'garage') {
			editedDevice.closedPositionRange = +deviceData.closedPositionRange.toFixed(0);
			editedDevice.closedToleranceRange = +deviceData.closedToleranceRange.toFixed(0);
		} else if (deviceData.modeType === 'occupancy') {
			editedDevice.triggerDistance = +deviceData.meterRange.toFixed(0);
		} else {
			editedDevice.meterRangeStart = +deviceData.meterRangeStart.toFixed(0);
			editedDevice.meterRangeEnd = +deviceData.meterRangeEnd.toFixed(0);
		}
		if ($app) {
			editedDevice.distanceRange = deviceData.distanceRange;
			editedDevice.measureInterval = deviceData.measureInterval;
			editedDevice.measureVibration = deviceData.measureVibration;
			hideBluetoothSettingsToast.set(true);
			if ($controlBleDevice.connectionState === 2) {
				if ($os === 'IOS') {
					control_bluetooth_general_ios(
						device.id,
						'setMeasureInterval',
						'measureInterval',
						deviceData.measureInterval,
					);
				} else {
					control_bluetooth_general_android(
						device.id,
						'setMeasureInterval',
						'measureInterval',
						deviceData.measureInterval,
					);
				}
			} else {
				$os === 'IOS'
					? control_bluetooth_device_ios(device.id, 'connect')
					: control_bluetooth_device_android(device.id, 'connect');
			}
		}
		editedDevice = {
			...editedDevice,
			...deviceSlotsParams(
				deviceData.modeType === 'garage'
					? 'garage'
					: deviceData.modeType === 'occupancy'
						? 'occupancy'
						: 'level',
			),
		};

		try {
			const res = await updateDeviceDistanceSettings(editedDevice);
			if (!res.isok) return showErrorToast();

			const mergedDevice = {
				...res.data,
				...Object.fromEntries(
					Object.entries(editedDevice).filter(([key]) => key !== 'cloud_options'),
				),
			};

			const saveRes = await saveDevice(mergedDevice);
			if (!saveRes.isok) return showErrorToast();
			devices.update(mergedDevice as Device);
			showSuccessToast();
		} catch (error) {
			showErrorToast();
		} finally {
			onCloseModal(true);
		}
	};

	$: disableNextWizard = (deviceData, activeStep, i) => {
		if (deviceData.modeType === 'garage') {
			if (errors.errorGarage) {
				if (activeStep.activeIndex === 2) {
					disabled = true;
				} else {
					disabled = false;
				}
				return i > 2;
			}

			disabled = false;
			return false;
		}

		if (!deviceData.isValid && activeStep.activeIndex === 0) {
			disabled = false;
			return i > 1;
		}
		if (!deviceData.isValid && activeStep.activeIndex === 1) {
			disabled = true;
			return i > activeStep.activeIndex;
		}
		if (deviceData.modeType === 'occupancy' && errors.error && activeStep.activeIndex === 2) {
			disabled = true;
			return i >= activeStep.activeIndex;
		}
		if (
			deviceData.modeType !== 'occupancy' &&
			(errors.errorMin || errors.errorMax) &&
			(activeStep.activeIndex === 0 || activeStep.activeIndex === 1)
		) {
			disabled = false;
			return i > 2;
		}
		if (
			deviceData.modeType !== 'occupancy' &&
			(errors.errorMin || errors.errorMax) &&
			activeStep.activeIndex === 2
		) {
			disabled = true;
			return i > activeStep.activeIndex;
		}

		disabled = false;
	};

	$: switch ($controlBleDevice.currentStatusIndex) {
		case null: // no state
			reverbBleProcess();
			break;
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
			initialized = false;
			break;
		// on device_processed (2) add the initialized state
		case BLU_STATUS.DEVICE_PROCESSED:
			showExpiredMsg = true;
			initialized = true;
			break;
		// on disconnect from the device start the process again
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			reverbBleProcess();
			break;
		default:
			break;
	}
</script>

<Modal
	on:close={() => onCloseModal()}
	{showModal}
	title={$format('wizard_alert_cta', { default: 'Quick Tour Guide' })}
	backdropClose={false}
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={(e) => {
					onClickWizardItem(e, i);
				}}
				active={item.active}
				title={$format(item.titleKey, { default: item.title })}
				disabled={disableNextWizard(deviceData, activeStep, i)}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer activeStepIndex={activeStep.activeIndex} adaptiveHeight>
			{#each wizardItems as item, i (i)}
				<WizardContent active={activeStep.activeIndex === i}>
					<div class="mb-m d-flex align-items-center">
						<Text styleType="h2" class="mr-s">
							{$format(item.subtitleKey, { default: item.subtitle })}
						</Text>
						{#if i === 1}
							<Popover type="hover" hideCloseBtn placement="bottom">
								<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
								<Text slot="customContent" styleType="body-extra-small">
									{$format('text_wizard_install_height', {
										default:
											'The ultrasonic distance sensor measures within a 30° cone, so keep nearby walls or obstacles clear to avoid false readings and ensure accurate distance measurement.',
									})}
								</Text>
							</Popover>
						{:else if (i === 3 && deviceData.modeType === 'garage') || i === 4}
							<Popover type="hover" hideCloseBtn placement="bottom">
								<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
								<Text slot="customContent" styleType="body-extra-small">
									{$format('popover_text_measurement_wizard', {
										default:
											'The sensor checks only the selected distance band and measures at your chosen interval. Narrower range or longer interval = better battery. Unsure? Keep Full Range and 30s.',
									})}
								</Text>
							</Popover>
						{/if}
					</div>
					<hr class="mb-m" />
					<svelte:component
						this={mappedWizardSteps[i]}
						bind:deviceData
						{device}
						{initialized}
						{showExpiredMsg}
						on:error={(e) => (errors = e.detail)}
						on:select={(e) => (deviceData = { ...defaultDeviceData, modeType: e.detail })}
					/>
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeStep.activeIndex > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => {
					onClickWizardItem(null, activeStep.activeIndex - 1);
				}}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			{disabled}
			aria-label={activeStep.activeIndex < wizardItems.length - 1
				? $format('button_next', { default: 'Next' })
				: $format('button_save', { default: 'Save' })}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				if (activeStep.activeIndex < wizardItems.length - 1) {
					onClickWizardItem(null, activeStep.activeIndex + 1);
				} else {
					onCloseWizard();
				}
			}}
		>
			{#if activeStep.activeIndex < wizardItems.length - 1}
				{$format('button_next', { default: 'Next' })}
			{:else}
				{$format('button_save', { default: 'Save' })}
			{/if}
		</Button>
	</div>
</Modal>
